import { DestroyRef, Injectable, inject } from "@angular/core";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { patchState, signalStore, withState } from "@ngrx/signals";
import { catchError, defer, finalize, of } from "rxjs";
import type { BranchSelectionItem, BranchSelectionQuery } from "../models/branches/branch.model";
import { Permission } from "../models/enums/permission.enum";
import { Provider } from "../models/warranties/provide-res.interface";
import { IProviderBranches } from "../models/warranties/provider-branches.interface";
import { BranchService } from "../services/branch.service";
import { PermissionService } from "../services/permission.service";
import { ProviderService } from "../services/provider.service";
import { withRequestStatus } from "./features/with-request-status.feature";
import { withStorage } from "./features/with-storage.feature";
import { UserStore } from "./user.store";

export type ProviderState = {
  providerBranches: Record<string, IProviderBranches[]>;
  gcProvider: Array<Provider>;
  geProvider: Array<Provider>;
};

const initialState: ProviderState = {
  providerBranches: {},
  gcProvider: [],
  geProvider: [],
};
const STORAGE_KEY = "provider";

@Injectable({ providedIn: "root" })
export class ProviderStore extends signalStore({ protectedState: false }, withState(initialState), withRequestStatus(), withStorage(STORAGE_KEY, sessionStorage)) {
  storeId$ = toObservable(this.userStore.user.storeCode);
  private readonly destroyRef = inject(DestroyRef);
  private readonly permissionService = inject(PermissionService);

  constructor(
    private readonly providerService: ProviderService,
    private readonly branchService: BranchService,
    private readonly userStore: UserStore
  ) {
    super();
  }

  private activeLoadingRequests = 0;

  private beginLoading(): void {
    this.activeLoadingRequests += 1;
    patchState(this, { isLoading: true, hasError: false });
  }

  private endLoading(): void {
    this.activeLoadingRequests = Math.max(0, this.activeLoadingRequests - 1);
    if (this.activeLoadingRequests === 0) {
      patchState(this, { isLoading: false });
    }
  }

  initialize(): void {
    this.storeId$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      void this.getProviders();
    });
  }

  private getKey(sku?: string | null, subclass?: string | null): string {
    return sku?.trim() || subclass?.trim() || "unknown";
  }

  private transformProvidersIntoMap(sku: string, providers: IProviderBranches[]): Record<string, IProviderBranches[]> {
    const map: Record<string, IProviderBranches[]> = {};
    for (const provider of providers) {
      const key = sku;
      if (!map[key]) {
        map[key] = [];
      }
      map[key].push(provider);
    }
    return map;
  }

  getProviderBranchesBySkuAndSubclass(sku: string, subclass: string): void {
    const key = this.getKey(sku, subclass);

    if (this.providerBranches()[key]?.length > 0) {
      return;
    }
    this.beginLoading();

    defer(() => this.providerService.getProviders(sku, subclass))
      .pipe(
        catchError(() => of([])),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.endLoading())
      )
      .subscribe({
        next: (providers: Array<IProviderBranches>) => {
          patchState(this, {
            hasError: false,
            providerBranches: this.transformProvidersIntoMap(sku, providers),
          });
        },
        error: (err: unknown) => {
          patchState(this, { hasError: true });
          console.error(`Failed to fetch providers for key: ${key}`, err);
        },
      });
  }

  setProviderBranches(sku: string, branches: IProviderBranches[]): void {
    patchState(this, {
      providerBranches: {
        ...this.providerBranches(),
        [sku]: branches,
      },
    });
  }

  getProviderBranchesBySelection(sku: string, query: BranchSelectionQuery, providerName: string): void {
    this.beginLoading();

    defer(() => this.branchService.selectBranches(query))
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.endLoading())
      )
      .subscribe({
        next: (response) => {
          this.setProviderBranches(sku, this.transformSelectedBranches(response.branches, query, providerName));
          patchState(this, { hasError: false });
        },
        error: (error: unknown) => {
          patchState(this, { hasError: true });
          console.error(`Failed to select provider branches for key: ${sku}`, error);
        },
      });
  }

  private transformSelectedBranches(branches: BranchSelectionItem[], query: BranchSelectionQuery, providerName: string): IProviderBranches[] {
    return branches.map(
      (branch: BranchSelectionItem): IProviderBranches => ({
        branchName: branch.branchName,
        branchStatus: "",
        address: branch.address,
        providerRut: query.providerDni,
        providerName,
        brand: query.brand,
        districtName: branch.commune ?? "",
        districtNumber: -1,
        skuOrSubClass: query.subCategory ?? "",
        regionName: "",
        regionNumber: -1,
        idSucursal: branch.externalId ?? branch.id,
      })
    );
  }

  getProviders(): void {
    const canLoadGe = this.permissionService.hasAny([Permission.CREATE_OST_GATEX_MT, Permission.CREATE_OST_GATEX_BT]);
    const canLoadGc = this.permissionService.hasAny([Permission.CREATE_OST_CONNECT_DANO, Permission.CREATE_OST_CONNECT_ROBO]);

    if (canLoadGe && this.geProvider().length === 0) {
      this.beginLoading();
      defer(() => this.providerService.getProviderGE())
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => this.endLoading())
        )
        .subscribe({
          next: (geProvider: Array<Provider>) => {
            patchState(this, { geProvider });
          },
          error: (err: unknown) => {
            patchState(this, { hasError: true });
            console.error("Failed to fetch geProvider:", err);
          },
        });
    }
    if (canLoadGc && this.gcProvider().length === 0) {
      this.beginLoading();
      defer(() => this.providerService.getProviderGC())
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => this.endLoading())
        )
        .subscribe({
          next: (gcProvider: Array<Provider>) => {
            patchState(this, { gcProvider });
          },
          error: (err: unknown) => {
            patchState(this, { hasError: true });
            console.error("Failed to fetch gcProvider:", err);
          },
        });
    }
  }

  getProvidersBranches(sku: string): IProviderBranches[] {
    return this.providerBranches()[sku] ?? [];
  }

  getProvidersData(): { gcProvider: Provider[]; geProvider: Provider[] } {
    return {
      gcProvider: this.gcProvider(),
      geProvider: this.geProvider(),
    };
  }
}
